import { useMemo } from "react";
import { useIdentityConflicts } from "@/lib/hooks/use-flow-instances/use-sumsub-verification";
import type { ComponentStyleType } from "@/lib/types/flow";
import { SumsubVerificationWidget } from "../sumsub-verification/sumsub-verification-widget";

type WizardSumsubSubjectProps = {
  instanceId: string;
  componentId: string;
  /** The person verified (a representative's `client_party.id` or member id). */
  subjectId: string;
  name: string | null;
  style?: ComponentStyleType;
  language: string;
  /** The whole saved value (it carries every person's verification in `subjects`). */
  calculatedData: string | null;
  onRefetchValue: () => Promise<string | null>;
  disabled?: boolean;
  hasValidationError?: boolean;
};

type StoredSubject = { input?: unknown; result?: unknown };

/** One person's slice of the saved value, as the single-person widget reads it. */
function sliceFor(raw: string | null, subjectId: string): StoredSubject | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { subjects?: Record<string, StoredSubject> };
    return parsed.subjects?.[subjectId] ?? null;
  } catch {
    return null;
  }
}

/**
 * QL-654 — one person's Sumsub verification inside a multi-person flow: the
 * ordinary widget, fed that person's own slice of the value and sending their
 * `subjectId` on every access-token and conflict request.
 */
export function WizardSumsubSubject({
  instanceId,
  componentId,
  subjectId,
  name,
  style = "none",
  language,
  calculatedData,
  onRefetchValue,
  disabled,
  hasValidationError,
}: WizardSumsubSubjectProps) {
  const slice = useMemo(() => sliceFor(calculatedData, subjectId), [calculatedData, subjectId]);
  const ownCalculated = slice?.result ? JSON.stringify(slice.result) : null;
  const ownInput = slice?.input ? JSON.stringify(slice.input) : null;
  const conflicts = useIdentityConflicts(instanceId, ownCalculated, subjectId);

  const handleRefetchValue = async () => sliceForRefetch(await onRefetchValue(), subjectId);

  return (
    <SumsubVerificationWidget
      componentId={componentId}
      subjectId={subjectId}
      style={style}
      title={name ?? undefined}
      language={language}
      inputData={ownInput}
      calculatedData={ownCalculated}
      onRefetchValue={handleRefetchValue}
      accessTokenUrl={`/internal/flow-instances/${instanceId}/sumsub-verification/access-token`}
      identityConflicts={conflicts.rows}
      onResolveIdentityConflict={conflicts.resolve}
      disabled={disabled}
      hasValidationError={hasValidationError}
    />
  );
}

function sliceForRefetch(raw: string | null, subjectId: string): string | null {
  const slice = sliceFor(raw, subjectId);
  return slice?.result ? JSON.stringify(slice.result) : null;
}
